import { COOKIES } from '@belong/constants';
import { getCookie, readCookie, writeCookie } from '@belong/cookies';
import { GetServerSidePropsContext, NextPageContext } from 'next';

const COOKIE_EXPIRY_YEARS = 100;

/**
 * Checks if a specific alert has already been dismissed.
 *
 * @param alertCode - The unique code identifying the alert.
 * @param persistCookie - If true, checks the persisted cookie for the alert. if false, checks the session cookie.
 * @param context - The context object, if available (e.g., for server-side rendering)
 * @returns true if the alert has been dismissed, false otherwise.
 */
export const hasAlertBeenDismissed = (
  alertCode: string,
  persistCookie: boolean = false,
  context?: NextPageContext | GetServerSidePropsContext
): boolean => {
  const cookieKey = persistCookie ? COOKIES.PERSISTED_DISMISSED_ALERTS : COOKIES.DISMISSED_ALERTS;
  const dismissedAlerts =
    context !== undefined ? getCookie(cookieKey, context, { parse: true }) : readCookie(cookieKey);
  return !!dismissedAlerts?.[alertCode];
};

/**
 * Stores a dismissed alert code in a cookie.
 * Optionally sets an expiry date based on the number of years provided.
 *
 * @param alertCode - The unique code of the alert to be stored.
 * @param persistCookie - If false or omitted, the cookie will be a session cookie
 *                        If true the cookie will remain for long time ie. 100 years
 */
export const storeDismissedAlertCode = (alertCode: string, persistCookie: boolean = false): void => {
  const cookieKey = persistCookie ? COOKIES.PERSISTED_DISMISSED_ALERTS : COOKIES.DISMISSED_ALERTS;
  const dismissedAlerts = readCookie(cookieKey) || {};

  const cookieOptions: { expires?: Date; path: string } = { path: '/' };

  if (persistCookie) {
    const expiryDate = new Date();
    expiryDate.setFullYear(expiryDate.getFullYear() + COOKIE_EXPIRY_YEARS);
    cookieOptions.expires = expiryDate;
  }

  writeCookie(cookieKey, { ...dismissedAlerts, [alertCode]: 1 }, cookieOptions);
};
